import { MISSIONS, Mission } from '../missions/MissionDefinitions';
import { TransmissionType } from '../vehicle/Powertrain';

export interface StartExamConfig {
  missionId: string;
  transmission: TransmissionType;
}

export class ModeSelectModal {
  public container: HTMLElement;
  private selectedMissionId: string = 'vollpruefung';
  private selectedTransmission: TransmissionType = 'automatic';
  private onStartCallback: ((config: StartExamConfig) => void) | null = null;
  private onOpenSettingsCallback: (() => void) | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'mode-select-modal';
    this.container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(9, 13, 22, 0.28);
      backdrop-filter: blur(1.5px);
      z-index: 40;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 16px;
      pointer-events: auto;
    `;

    this.buildUI();
  }

  private buildUI() {
    this.container.innerHTML = '';
    const card = document.createElement('div');
    card.id = 'mode-select-card';
    card.style.cssText = `
      background: #0f172a;
      border: 1px solid #334155;
      border-radius: 20px;
      width: min(680px, 95vw);
      max-height: 94vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding: clamp(14px, 3.5vw, 26px);
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.8);
      display: flex;
      flex-direction: column;
      gap: 16px;
      box-sizing: border-box;
    `;

    // Header with Settings & APK action buttons
    const header = document.createElement('div');
    header.style.cssText = `display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:10px;`;
    header.innerHTML = `
      <div style="display:flex;align-items:center;gap:10px;">
        <span style="font-size:28px;">🚗</span>
        <div>
          <h1 style="font-size: clamp(17px, 3vw, 22px);font-weight:800;color:#f8fafc;margin:0;">Fahrschul-Simulator StVO 3D</h1>
          <p style="font-size:12px;color:#94a3b8;margin:2px 0 0 0;">Praktische Fahrprüfung nach deutscher StVO</p>
        </div>
      </div>
    `;

    const topBtns = document.createElement('div');
    topBtns.style.cssText = `display:flex;gap:8px;align-items:center;`;

    const btnSettings = document.createElement('button');
    btnSettings.innerHTML = '⚙️ Einstellungen';
    btnSettings.style.cssText = `
      background: #1e293b;
      color: #38bdf8;
      border: 1px solid #0284c7;
      padding: 10px 14px;
      min-height: 44px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      touch-action: manipulation;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
    `;
    btnSettings.addEventListener('click', () => {
      if (this.onOpenSettingsCallback) this.onOpenSettingsCallback();
    });

    const btnApk = document.createElement('a');
    btnApk.href = '/app-debug.apk';
    btnApk.download = 'FahrschulSimulatorStVO.apk';
    btnApk.innerHTML = '📱 APK';
    btnApk.style.cssText = `
      background: #1e3a8a;
      color: #93c5fd;
      text-decoration: none;
      border: 1px solid #3b82f6;
      padding: 10px 14px;
      min-height: 44px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      touch-action: manipulation;
      box-sizing: border-box;
    `;

    topBtns.appendChild(btnSettings);
    topBtns.appendChild(btnApk);
    header.appendChild(topBtns);
    card.appendChild(header);

    // 1. Transmission Selection (Automatic vs Manual)
    const transSection = document.createElement('div');
    transSection.innerHTML = `<h3 style="font-size:13px;font-weight:700;color:#38bdf8;text-transform:uppercase;margin-bottom:8px;">1. Getriebeart wählen</h3>`;

    const transRow = document.createElement('div');
    transRow.style.cssText = `display: grid; grid-template-columns: 1fr 1fr; gap: 12px;`;

    const btnAuto = document.createElement('div');
    btnAuto.style.cssText = this.getOptionBoxStyle(this.selectedTransmission === 'automatic');
    btnAuto.innerHTML = `
      <div style="font-size:20px;">⚡</div>
      <div style="font-weight:700;color:#f8fafc;">Automatik (P-R-N-D)</div>
      <div style="font-size:12px;color:#94a3b8;">Schaltet automatisch, kein Abwürgen</div>
    `;
    btnAuto.addEventListener('click', () => {
      this.selectedTransmission = 'automatic';
      btnAuto.style.cssText = this.getOptionBoxStyle(true);
      btnManual.style.cssText = this.getOptionBoxStyle(false);
    });

    const btnManual = document.createElement('div');
    btnManual.style.cssText = this.getOptionBoxStyle(this.selectedTransmission === 'manual');
    btnManual.innerHTML = `
      <div style="font-size:20px;">🕹️</div>
      <div style="font-weight:700;color:#f8fafc;">Schaltwagen (1-6 + Kupplung)</div>
      <div style="font-size:12px;color:#94a3b8;">Kupplungspedal, Schleifpunkt & Motor-Abwürgen</div>
    `;
    btnManual.addEventListener('click', () => {
      this.selectedTransmission = 'manual';
      btnManual.style.cssText = this.getOptionBoxStyle(true);
      btnAuto.style.cssText = this.getOptionBoxStyle(false);
    });

    transRow.appendChild(btnAuto);
    transRow.appendChild(btnManual);
    transSection.appendChild(transRow);
    card.appendChild(transSection);

    // 2. Mission Selection
    const missionSection = document.createElement('div');
    missionSection.innerHTML = `<h3 style="font-size:13px;font-weight:700;color:#38bdf8;text-transform:uppercase;margin-bottom:8px;">2. Prüfungsaufgabe wählen</h3>`;

    const missionList = document.createElement('div');
    missionList.style.cssText = `display: flex; flex-direction: column; gap: 8px; max-height: 220px; overflow-y: auto; padding-right: 4px;`;

    MISSIONS.forEach((m) => {
      const item = document.createElement('div');
      item.setAttribute('data-mission-id', m.id);
      const isSelected = m.id === this.selectedMissionId;
      item.style.cssText = this.getMissionItemStyle(isSelected);
      item.innerHTML = `
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <span style="font-weight:700;color:#f8fafc;font-size:14px;">${m.title}</span>
          <span style="font-size:11px;font-weight:600;padding:2px 8px;border-radius:6px;background:${m.category === 'Grundfahraufgabe' ? '#1e3a8a' : '#14532d'};color:#93c5fd;">${m.category}</span>
        </div>
        <div style="font-size:12px;color:#94a3b8;margin-top:2px;">${m.description}</div>
      `;
      item.addEventListener('click', () => {
        this.selectedMissionId = m.id;
        Array.from(missionList.children).forEach((c, idx) => {
          (c as HTMLElement).style.cssText = this.getMissionItemStyle(MISSIONS[idx].id === this.selectedMissionId);
        });
      });
      missionList.appendChild(item);
    });

    missionSection.appendChild(missionList);
    card.appendChild(missionSection);

    // 3. Start Button
    const startBtn = document.createElement('button');
    startBtn.id = 'start-exam-btn';
    startBtn.innerText = '🚀 Fahrprüfung starten';
    startBtn.style.cssText = `
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
      color: white;
      border: none;
      border-radius: 12px;
      padding: 16px;
      min-height: 52px;
      font-size: 16px;
      font-weight: 800;
      cursor: pointer;
      touch-action: manipulation;
      box-shadow: 0 8px 24px rgba(37, 99, 235, 0.4);
      transition: transform 0.1s, filter 0.1s;
      box-sizing: border-box;
      width: 100%;
    `;
    startBtn.addEventListener('click', () => {
      if (this.onStartCallback) {
        this.onStartCallback({
          missionId: this.selectedMissionId,
          transmission: this.selectedTransmission,
        });
      }
    });

    card.appendChild(startBtn);
    this.container.appendChild(card);
  }

  private getOptionBoxStyle(selected: boolean): string {
    return `
      background: ${selected ? '#1e293b' : '#090d16'};
      border: 2px solid ${selected ? '#38bdf8' : '#334155'};
      border-radius: 12px;
      padding: 14px;
      min-height: 56px;
      cursor: pointer;
      touch-action: manipulation;
      display: flex;
      flex-direction: column;
      gap: 4px;
      box-sizing: border-box;
      transition: all 0.15s ease;
    `;
  }

  private getMissionItemStyle(selected: boolean): string {
    return `
      background: ${selected ? '#1e293b' : '#090d16'};
      border: 1.5px solid ${selected ? '#3b82f6' : '#1e293b'};
      border-radius: 10px;
      padding: 12px 14px;
      min-height: 52px;
      cursor: pointer;
      touch-action: manipulation;
      box-sizing: border-box;
      transition: all 0.15s ease;
    `;
  }

  public onStart(cb: (config: StartExamConfig) => void) {
    this.onStartCallback = cb;
  }

  public onOpenSettings(cb: () => void) {
    this.onOpenSettingsCallback = cb;
  }

  public mount(root: HTMLElement) {
    this.buildUI();
    root.appendChild(this.container);
  }

  public unmount() {
    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
  }
}
